import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy, where, getDoc } from "firebase/firestore";
import ConfirmationModal from '../componenets/ConfirmationModal';
import UpdateModal from '../componenets/UpdateModal';
import CreatePostModal from '../componenets/CreatePostModal';
import TrackInfoEditor from '../componenets/TrackInfoEditor';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

const Dashboard = () => {
    const navigate = useNavigate();
    const [blogPosts, setBlogPosts] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [postToEdit, setPostToEdit] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [adminTrack, setAdminTrack] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const openCreateModal = () => {
        setIsCreateModalOpen(true);
    }

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
    }

    const openEditModal = (postId) => {
        const post = blogPosts.find(post => post.id === postId);
        setPostToEdit(post)
        setIsEditModalOpen(true);
    }
    
    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setPostToEdit(null);
    }

    const openConfirmationModal = (postId) => {
        setPostToDelete(postId)
        setIsModalOpen(true);
    }

    const closeConfirmationModal = () => {
        setIsModalOpen(false);
        setPostToDelete(null);
    }

    const confirmAction = async () => {
        if(postToDelete) {
            await deletePost(postToDelete);

            setBlogPosts(blogPosts.filter(post => post.id !== postToDelete));
            setIsModalOpen(false);
            setPostToDelete(null);
        }
    }

    const handleEditPost = async (updatedPost) => {
        if (postToEdit) {
            const postDoc = doc(db, 'media', updatedPost.id);
            await updateDoc(postDoc, updatedPost);

            const updatedPosts = blogPosts.map(post => 
                post.id === updatedPost.id ? updatedPost : post
            );
            setBlogPosts(updatedPosts);
            setIsEditModalOpen(false);
            setPostToEdit(null);
        }
    }

    const deletePost = async (postId) => {
        try {
            const postDoc = doc(db, "media", postId);
            await deleteDoc(postDoc);
        } catch(error) {
            console.error("Error deleting the post: ", error);
        }
    }

    const fetchBlogPosts = async (track) => {
        try {
            const blogQuery = query(
                collection(db, 'media'),
                where('track', '==', track),
                orderBy('timestamp', 'desc'),
            );
            const blogSnapshot = await getDocs(blogQuery);
            const blogList = blogSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setBlogPosts(blogList);
            setFetchError(null);
        } catch (err) {
            console.error("fetchBlogPosts failed", err);
            setFetchError(err.message || String(err));
            setBlogPosts([]);
        }
    };

    useEffect(() => {
        const checkAuthState = async(user) => {
            if (!user) {
                navigate('/login');
                setLoading(false);
                return;
            }
            const userDoc = await getDoc(doc(db, "users", user.uid));
            const data = userDoc.exists() ? userDoc.data() : null;
            if (!data || !data.isAdmin || typeof data.track !== "string" || !data.track) {
                navigate('/login');
                setLoading(false);
                return;
            }
            setAdminTrack(data.track);
            setLoading(false);
        };

        const unsubscribe = onAuthStateChanged(auth, checkAuthState);

        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        if (adminTrack) {
            fetchBlogPosts(adminTrack);
        }
    }, [adminTrack]);

    const handlePostCreated = () => {
        if (adminTrack) fetchBlogPosts(adminTrack);
        closeCreateModal();
    }

    if (loading) return <p>Loading...</p>;
    if (!adminTrack) return <p>Access Denied</p>;

    return (
        <div className="h-full bg-neutral-100 m-0 px-6 pb-8 sm:px-12 pt-20">
            <div className='fixed border-b-2 w-full left-0 bg-neutral-100 px-6 sm:px-12 z-10 top-0 py-4 flex mb-4 justify-between gap-4 items-center'>
                <button onClick={() => navigate('/')} className='bg-sky-500 flex items-center gap-1 text-white font-medium p-2 rounded-lg hover:bg-sky-600'>
                    <svg width="16px" height="16px" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg"><path d="M768 903.232l-50.432 56.768L256 512l461.568-448 50.432 56.768L364.928 512z" fill="#fff" /></svg>
                    Go to Media Feed
                </button>
                <div className="text-sm text-gray-700">
                    Managing track · <span className="font-semibold text-gray-900">{adminTrack}</span>
                </div>
                <button onClick={openCreateModal} className='bg-sky-500 text-white font-medium p-2 rounded-lg hover:bg-sky-600'>Create Post</button>
            </div>
            <div className='flex flex-col md:flex-row gap-6'>
                <div className='flex flex-col w-full md:w-1/3 mx-auto mb-8'>
                    <TrackInfoEditor track={adminTrack} />
                </div>
                <div className="w-full overflow-auto rounded-3xl mt-1">
                    {fetchError && (
                        <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm p-3 text-left">
                            Posts failed to load: {fetchError}
                        </div>
                    )}
                    <table className="min-w-full bg-white rounded-lg">
                        <thead>
                            <tr>
                                <th className="py-4 text-gray-600 text-md px-4 border-b border-gray-300 text-left">Title</th>
                                <th className="py-4 text-gray-600 text-md px-4 border-b border-gray-300 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {blogPosts.map((campaign) => (
                                <tr key={campaign.id} className="hover:bg-gray-100">
                                    <td className="py-4 px-4 border-b border-gray-300 align-top">
                                        <div className="text-medium font-semibold text-left mb-1 sm:text-xl">
                                            {campaign.title}
                                        </div>
                                        <div className="text-sm line-clamp-2 text-left text-[#828793]">
                                            {campaign.description}
                                        </div>
                                        <div className="text-medium mt-1 text-violet-600 font-semibold text-left text-black">
                                            Interactions: {campaign.clicks}
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 border-b border-gray-300 align-center">
                                        <div className="flex flex-col gap-2 lg:flex-row md:gap-4">
                                            <button
                                                className="bg-sky-500 text-white text-base w-12 h-12 flex justify-center items-center rounded-full scale-90 sm:scale-100"
                                                onClick={() => openEditModal(campaign.id)}
                                            >
                                                <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M20.8477 1.87868C19.6761 0.707109 17.7766 0.707105 16.605 1.87868L2.44744 16.0363C2.02864 16.4551 1.74317 16.9885 1.62702 17.5692L1.03995 20.5046C0.760062 21.904 1.9939 23.1379 3.39334 22.858L6.32868 22.2709C6.90945 22.1548 7.44285 21.8693 7.86165 21.4505L22.0192 7.29289C23.1908 6.12132 23.1908 4.22183 22.0192 3.05025L20.8477 1.87868ZM18.0192 3.29289C18.4098 2.90237 19.0429 2.90237 19.4335 3.29289L20.605 4.46447C20.9956 4.85499 20.9956 5.48815 20.605 5.87868L17.9334 8.55027L15.3477 5.96448L18.0192 3.29289ZM13.9334 7.3787L3.86165 17.4505C3.72205 17.5901 3.6269 17.7679 3.58818 17.9615L3.00111 20.8968L5.93645 20.3097C6.13004 20.271 6.30784 20.1759 6.44744 20.0363L16.5192 9.96448L13.9334 7.3787Z" fill="#fff"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="bg-rose-600 text-white text-base w-12 h-12 flex justify-center items-center rounded-full scale-90 sm:scale-100"
                                                onClick={() => openConfirmationModal(campaign.id)}
                                            >
                                                <svg fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px"><path d="M 10 2 L 9 3 L 5 3 C 4.4 3 4 3.4 4 4 C 4 4.6 4.4 5 5 5 L 7 5 L 17 5 L 19 5 C 19.6 5 20 4.6 20 4 C 20 3.4 19.6 3 19 3 L 15 3 L 14 2 L 10 2 z M 5 7 L 5 20 C 5 21.1 5.9 22 7 22 L 17 22 C 18.1 22 19 21.1 19 20 L 19 7 L 5 7 z M 9 9 C 9.6 9 10 9.4 10 10 L 10 19 C 10 19.6 9.6 20 9 20 C 8.4 20 8 19.6 8 19 L 8 10 C 8 9.4 8.4 9 9 9 z M 15 9 C 15.6 9 16 9.4 16 10 L 16 19 C 16 19.6 15.6 20 15 20 C 14.4 20 14 19.6 14 19 L 14 10 C 14 9.4 14.4 9 15 9 z"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <ConfirmationModal
                    isOpen={isModalOpen}
                    onClose={closeConfirmationModal}
                    onConfirm={confirmAction}
                />
            <UpdateModal
                    isOpen={isEditModalOpen}
                    onClose={closeEditModal}
                    onConfirm={handleEditPost}
                    data={postToEdit}
                />
            <CreatePostModal
                isOpen={isCreateModalOpen}
                onClose={closeCreateModal}
                onPostCreated={handlePostCreated}
                track={adminTrack}
            />
        </div>
    );
};

export default Dashboard;
